import styled from "styled-components";
import { fontStyles } from "../../../../../Component/style/admin_font";
import { CenterView, RowView } from "../../../../../Component/style/common_style";
import { PopupState } from "./useExcelUpload";

/** 엑셀 업로드 내부 팝업(확인/확인·취소/이미지목록) 공통 렌더러 */
const ExcelPopup = ({
  popup,
  onClose,
}: {
  popup: PopupState;
  onClose: () => void;
}) => {
  const hasCancel = !!popup.cancelText;

  const handleConfirm = () => {
    popup.onConfirm?.();
    onClose();
  };

  return (
    <Overlay onClick={onClose}>
      <Box onClick={(e) => e.stopPropagation()}>
        <Title>{popup.title}</Title>
        {popup.errorCount !== undefined ? (
          <Message>
            <Red>{popup.errorCount}건</Red>의 오류가 있습니다.{"\n"}
            무시하고 업로드를 완료할까요?
          </Message>
        ) : (
          popup.message && <Message>{popup.message}</Message>
        )}

        {popup.images && (
          <ImageList>
            {popup.images.map((img) => (
              <ImageRow key={img.name}>
                <Thumb src={img.url} alt={img.name} />
                <Name title={img.name}>{img.name}</Name>
              </ImageRow>
            ))}
          </ImageList>
        )}

        <Buttons>
          {hasCancel && (
            <CancelButton onClick={onClose}>{popup.cancelText}</CancelButton>
          )}
          <ConfirmButton onClick={handleConfirm}>
            {popup.confirmText ?? "확인"}
          </ConfirmButton>
        </Buttons>
      </Box>
    </Overlay>
  );
};

export default ExcelPopup;

const Overlay = styled(CenterView)`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1100;
`;

const Box = styled.div`
  box-sizing: border-box;
  width: 90%;
  max-width: 20rem;
  background: var(--admin-theme-surface, white);
  border-radius: 12px;
  padding: 1.6rem 1.4rem 1.2rem;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
`;

const Title = styled.div`
  text-align: center;
  font-size: 16px;
  ${fontStyles.Bold}
  color: var(--admin-ink);
  margin-bottom: 1rem;
`;

const Message = styled.div`
  text-align: center;
  font-size: 14px;
  color: var(--admin-theme-muted, #6b6b6b);
  line-height: 1.6;
  white-space: pre-line;
  margin-bottom: 1.4rem;
  ${fontStyles.regular}
`;

const Red = styled.span`
  color: var(--admin-theme-danger, #eb003b);
  ${fontStyles.semiBold}
`;

const ImageList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 15rem;
  overflow-y: auto;
  margin-bottom: 1.4rem;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--admin-theme-raised, #e2e2e2);
    border-radius: 4px;
  }
`;

const ImageRow = styled(RowView)`
  align-items: center;
  padding: 0.4rem 0;
`;

const Thumb = styled.img`
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid var(--admin-theme-border, #f0f0f0);
`;

const Name = styled.div`
  flex: 1;
  margin-left: 0.8rem;
  font-size: 13px;
  color: var(--admin-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Buttons = styled(RowView)`
  gap: 0.6rem;
`;

const CancelButton = styled(CenterView)`
  flex: 1;
  height: 2.9rem;
  border-radius: 8px;
  background: var(--admin-theme-raised, #f0f0f0);
  color: var(--admin-muted);
  font-size: 14px;
  ${fontStyles.semiBold}
  cursor: pointer;
  &:hover {
    background: var(--admin-theme-raised, #e8e8e8);
  }
`;

const ConfirmButton = styled(CenterView)`
  flex: 1;
  height: 2.9rem;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--admin-accent), var(--admin-accent-deep));
  color: white;
  font-size: 14px;
  ${fontStyles.Bold}
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;
